import React from 'react';
import { Household } from '../domain';
import { useMembersDialog } from '../hooks/useMembersDialog';
import { Alert, Avatar, Badge, Button, ConfirmDialog, Dialog, DialogBody, DialogFooter, Switch } from './ui';

interface MembersDialogProps {
  household: Household;
  isOpen: boolean;
  onClose: () => void;
}

/** The household's members, for its owner to remove any of them but themselves, or mark them as children. */
const MembersDialog: React.FC<MembersDialogProps> = ({ household, isOpen, onClose }) => {
  const {
    members, isOnline, error, removing, isRemoving, askToRemove, cancelRemove, confirmRemove, changeChild, close,
  } = useMembersDialog(household, onClose);

  return (
    <>
      <Dialog open={isOpen} onClose={close} title={`Members of ${household.name}`}>
        <DialogBody>
          {!isOnline && <Alert tone="warning">You must be online to remove members or change who's a child.</Alert>}
          {error && <Alert tone="error">{error}</Alert>}

          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {members.map((member) => (
              <li key={member.id} className="flex items-center gap-3 py-2.5">
                <Avatar name={member.name} id={member.id} size="md" />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                  {member.name}
                </span>
                {member.isOwner
                  ? <Badge tone="brand" size="sm">Owner</Badge>
                  : (
                    <>
                      <Switch
                        checked={member.isChild}
                        onChange={(isChild) => changeChild(member, isChild)}
                        label="Child"
                        disabled={!isOnline || member.isChangingChild}
                      />
                      <Button size="sm" variant="danger-ghost" disabled={!isOnline} onClick={() => askToRemove(member)}>
                        Remove
                      </Button>
                    </>
                  )}
              </li>
            ))}
          </ul>
          {members.length === 1 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No one else is in this household yet.</p>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              A child sees only the chores assigned to them, and can only mark them done.
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={close}>Close</Button>
        </DialogFooter>
      </Dialog>

      <ConfirmDialog
        open={removing !== null}
        onClose={cancelRemove}
        onConfirm={confirmRemove}
        title={`Remove ${removing?.name ?? ''} from ${household.name}?`}
        message="They'll no longer see the household or its chores, and are taken off the chores they're assigned to. What they've done stays in the history. To bring them back, invite them again."
        confirmLabel="Remove"
        tone="danger"
        loading={isRemoving}
      />
    </>
  );
};

export default MembersDialog;
